import {
  getNotificationsKey,
  getUnreadNotificationsCountKey,
  NotificationLiteModel,
  useGetInfiniteNotifications,
  useGetUnreadNotificationsCount,
  useReadAllNotifications,
  useReadNotification,
} from 'api-hooks/notification';
import { AuthorizationRules } from 'common/constants';
import { NotificationTypeEnum } from 'common/constants/enum';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import navigateByAcl from 'components/pivot/navigate-by-acl';
import React from 'react';

import { useAuthorization } from './use-authorization';
import useNavigation from './use-navigation';

export default function useNotifications() {
  const { navigate } = useNavigation();
  const { can } = useAuthorization();
  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
    hasNextPage: hasMore,
  } = useGetInfiniteNotifications();

  const notifications = React.useMemo(() => {
    return (data?.pages || [])
      .map((page) => {
        return page.data;
      })
      .flat();
  }, [data?.pages]);

  const { data: unreadNotifications } = useGetUnreadNotificationsCount();
  const unreadNotificationsCount = unreadNotifications?.data.count || 0;

  const { mutateAsync: readAllNotifications, isLoading: readAllLoading } =
    useReadAllNotifications();
  const { mutateAsync: readNotification } = useReadNotification();

  const onReadAll = React.useCallback(
    async (close: () => void) => {
      try {
        const result = await readAllNotifications({});
        close();

        queryClient.refetchQueries([getNotificationsKey()[0]]);
        queryClient.refetchQueries([getUnreadNotificationsCountKey()[0]]);

        notification.success({ message: result.message });

        return result.message;
      } catch (error) {
        notification.error({ message: error.message });
      }
    },
    [readAllNotifications],
  );

  const permission = React.useCallback((type: NotificationTypeEnum) => {
    switch (type) {
      case 'document_sale_reminder':
        return AuthorizationRules.DocumentSalesView;
      case 'cruise_sale_reminder':
        return AuthorizationRules.CruiseSalesView;
      default:
        throw new Error(`Invalid type: ${type}`);
    }
  }, []);

  const [readNotificationId, setReadNotificationId] = React.useState('');
  const onReadNotification = React.useCallback(
    async (notif: NotificationLiteModel, close: () => void) => {
      try {
        setReadNotificationId(notif.id);
        const result = await readNotification({ id: notif.id });

        const acl = notif.data.metadata.acl.split(/(employee.|.list|.page)/)[2];
        if (can(permission(notif.data.type))) {
          navigate(navigateByAcl[acl], {
            id: notif.data.metadata.id,
          });
        }
        close();

        queryClient.refetchQueries([getNotificationsKey()[0]]);
        queryClient.refetchQueries([getUnreadNotificationsCountKey()[0]]);

        return result.message;
      } catch (error) {
        notification.error({ message: error.message });
      } finally {
        setReadNotificationId('');
      }
    },
    [can, navigate, permission, readNotification],
  );

  return {
    notifications,
    unreadNotificationsCount,
    hasMore,
    isFetching,
    isFetchingNextPage,
    readAllLoading,
    readNotificationId,
    onReadAll,
    onReadNotification,
    onLoadMore,
  };
}
